import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import styled from 'styled-components/macro';
import { useAccountId } from '../../../auth/useAccountId';

import { useActions } from '../../../Actions';
import Page, { PageButtonLink } from '../../../components/Page';
import { updateParams } from '../../../helpers/urlParams';
import namespace from '../namespace';

import { Allow } from '../../../auth';
import Icon from '../../../components/Icons';
import { LoadingOverlay } from '../../../components/Loading';
import Paging from '../../../components/Paging/Infinite';
import { H1 } from '../../../components/Typography';
import { getCurrentLanguage } from '../../../i18n';
import { State } from '../../../reducer';
import { useAccount } from '../../../settings/account/hooks';
import { useLocations } from '../../../settings/locations/locationsList/hooks';
import { colors } from '../../../styles/constants';
import usePayoutDestinations from '../../../util/usePayoutDestinations';
import { createSettlementsCsv, createSettlementsCsvPdf, downloadSettlement, settlementSelector } from '../actions';
import { Settlement } from '../types';
import Filters from './Filters';
import ListMessage from './ListMessage';
import PayoutsTable from './SettlementsTable';
import { VippsGuide } from './VippsGuide';

const getLastElementOrUndefined = (groupedSettlements: Settlement[][]): Settlement | undefined => {
    if (groupedSettlements.length === 0) {
        return undefined;
    }
    const lastGroup = groupedSettlements[groupedSettlements.length - 1];
    return lastGroup.find((settlement) => settlement);
};

const groupByMonths = (settlements: Settlement[]): Settlement[][] => {
    // assumes input is a sorted list with the newest settlements first
    return settlements.reduce<Settlement[][]>((reduced, settlement) => {
        const prevSettlement = getLastElementOrUndefined(reduced);
        if (prevSettlement && prevSettlement.settled_at.substring(0, 7) === settlement.settled_at.substring(0, 7)) {
            // add last group in reduced
            const lastMonthGroup = reduced[reduced.length - 1];
            if (lastMonthGroup) {
                lastMonthGroup.push(settlement);
                return reduced;
            }
        }
        return [...reduced, [settlement]];
    }, []);
};

const getPagingYear = (settlements: Settlement[]): number | undefined => {
    // assumes input is a sorted list with the newest settlements first
    if (settlements.length === 0) {
        return undefined;
    }
    return parseInt(settlements[settlements.length - 1].settled_at.substring(0, 4)) - 1;
};

const helpLink= (language: string) => {
    if (['no', 'se'].includes(language)) {
        return `https://www.dintero.com/${language}/payouts`;
    }
    return 'https://www.dintero.com/payouts';
};

const SettlementList = () => {
    const actions = useActions(namespace);
    const { t, i18n } = useTranslation();
    const accountId = useAccountId();
    const location = useLocation();
    const { account, isLoading: isLoadingAccounts } = useAccount();
    const langauge = getCurrentLanguage(i18n);

    const {
        params,
        settlements,
        isLoadingSettlements,
        limit,
        hasNext,
        hasPayoutDestinationId,
        hasSourceAttachments,
        hasStoreId,
    } = useSelector(settlementSelector);

    const isLoading = isLoadingSettlements || isLoadingAccounts;

    useEffect(() => {
        // on mount or account id changes
        // location params
        let fetchParams = new URLSearchParams(location.search.substr(1));
        fetchParams = updateParams(fetchParams, { limit: limit.toString() });
        actions.fetchSettlements(accountId, fetchParams, undefined);
    }, [accountId, actions, location, limit]);

    const { destinationIdLabelMap } = usePayoutDestinations();

    const { locations } = useLocations();

    const settlementGroups = useSelector<State, Settlement[][]>((s) => groupByMonths(s[namespace].settlements));
    const nextPagingYear = useSelector<State, number | undefined>((s) => getPagingYear(s[namespace].settlements));

    const nextParams = updateParams(params, { limit: limit.toString() });

    const next = () => {
        nextPagingYear && actions.fetchSettlements(accountId, nextParams, nextPagingYear);
    };

    const createCsv = () => {
        createSettlementsCsv(accountId, settlements, t, i18n.language, undefined, undefined, hasPayoutDestinationId ? destinationIdLabelMap : undefined);
    };

    const createPdf = () => {
        createSettlementsCsvPdf(accountId, settlements, t, getCurrentLanguage(i18n), undefined, undefined, hasPayoutDestinationId ? destinationIdLabelMap : undefined);
    };

    const history = useHistory();
    return (
        <Page
            title={
                <TitleWrapper>
                    <div>
                        <Title>{t('settlements.title')}</Title>
                        <ExternalLink href={helpLink(langauge)} target="_blank">{t('settlements.help_link_description')}</ExternalLink>
                    </div>
                    <Allow anyOfScopes={[['admin:accounts']]}  >
                        <PageButtonLink to={`/${accountId}/settings/payout-report-configs`}>
                            <Icon icon="document" fill="currentColor" />
                            {t('settlements.dintero_reports')}
                        </PageButtonLink>
                    </Allow>
                </TitleWrapper>
            }
            hasEnvFrame={false}
        >
            <VippsGuide
                isLoading={isLoading}
                queryParams={params}
                accountId={accountId}
                account={account}
                settlements={settlements}
            />
            <Filters history={history} location={location} createCsv={createCsv} createPdf={createPdf} />
            <PayoutsTable
                settlements={settlementGroups}
                accountId={accountId}
                downloadFile={downloadSettlement}
                createSettlementsCsv={createSettlementsCsv}
                createSettlementsCsvPdf={createSettlementsCsvPdf}
                locationQueryParams={params}
                hasPayoutDestinationId={hasPayoutDestinationId}
                hasStoreId={hasStoreId}
                locations={locations}
                hasSourceAttachments={hasSourceAttachments}
                destinationIdLabelMap={destinationIdLabelMap}
            />

            <RelativeGroup>
                <Paging
                    label={t('settlements.load_older', { year: nextPagingYear })}
                    isLoading={isLoading}
                    hasMore={hasNext}
                    onLoadMore={next}
                />
                <ListMessage
                    settlements={settlements}
                    isLoading={isLoading}
                    hasNext={hasNext}
                    isLoadingNext={false}
                    accountId={accountId}
                    queryParams={params}
                />
                {(isLoading) && <LoadingOverlay />}
            </RelativeGroup>
        </Page>
    );
};

const Title = styled(H1 as any)`
    display: inline-block;
        /* Heading/H6/Medium/Desktop */
        font-size: 20px;
    font-style: normal;
    font-weight: 500;
    line-height: 28px; /* 140% */
    letter-spacing: -0.4px;
`;
export default SettlementList;

const TitleWrapper = styled.div`
    display: flex;
    flex: 1;
    justify-content: space-between;

    @media (max-width: 768px) {
        flex-direction: column;
    }
`;


const RelativeGroup = styled.div`
    position: relative;
    overflow: hidden;
`;


const ExternalLink = styled.a`
    display: block;
    color: ${colors.primary};
    user-select: none;
    cursor: pointer;

    &:hover {
        color: ${colors.primaryHover};
    }
`;
